import { type JSX, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { DashletProps } from 'Components/PageLayout/Dashlet/Dashlet';
import { Text } from 'Components/Text/Text';
import { TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_BUSINESSES_AND_USE_CASES_LISTING } from 'Config/PathTemplates';
import { DashboardType, DueDiligenceDashboardAssessmentSubject } from 'Models/Dashboards';
import { ExceptionResponse } from 'Models/Exceptions';
import { IssueResponse } from 'Models/Issues';
import { Effectiveness, effectivenessLabelAsNumber } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating, riskRatingLabelAsNumber } from 'Models/RiskRating';

import { AssessmentSubjectsDisplayModal, SelectedAssessmentSubjectDetails } from './AssessmentSubjectsDisplayModal/AssessmentSubjectsDisplayModal';
import { CompletedAssessmentsChart } from './AssessmentsChart/CompletedAssessmentsChart';
import { UpcomingAssessmentsChart } from './AssessmentsChart/UpcomingAssessmentsChart';
import { CountCallout } from './CountCallout/CountCallout';
import { DonutCharts } from './DonutCharts/DonutCharts';
import { buildEffectivenessChartData, buildRiskRatingsChartData } from './DueDiligenceDashboard.helpers';
import { useActiveOrganizationsAndAssessmentSubjectsQuery } from './DueDiligenceDashboard.hooks';
import { PortfolioProfileOverTimeChart } from './PortfolioProfileOverTimeChart/PortfolioProfileOverTimeChart';
import { RiskProgressCharts } from './RiskProgressCharts/RiskProgressCharts';
import { DashboardsHistoricalExceptions } from '../Components/DashboardsHistoricalExceptions/DashboardsHistoricalExceptions';
import { DashboardsHistoricalIssues } from '../Components/DashboardsHistoricalIssues/DashboardsHistoricalIssues';
import { DashboardsTrackingExceptions } from '../Components/DashboardsTrackingExceptions/DashboardsTrackingExceptions';
import { DashboardsTrackingIssues } from '../Components/DashboardsTrackingIssues/DashboardsTrackingIssues';

type DisplayedModal = { type: 'none' } | { type: 'assessmentSubjects'; selectedAssessmentSubjects: SelectedAssessmentSubjectDetails[]; subtitle: string };

export interface UseDueDiligenceDashboardArgs {
    dashboardApi: DashboardApi;
    exceptionsError?: string;
    issuesError?: string;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
    userIsAuthorized: boolean;

    allExceptions?: ExceptionResponse[];
    allIssues?: IssueResponse[];
}

/**
 * Builds the TPRM or UCRM tab of the Dashboards page, which summarize the module's portfolio of third-party services / use cases.
 *
 * This is a hook rather than a component because `PageLayoutDashboardTabs` takes each tab's contents as an array of dashlets; wrapping a component in a single dashlet would draw an extra dashlet box around the entire tab.
 * The caller must render the returned `modal` itself (outside of the tabs).
 * Data is fetched only when `userIsAuthorized` is true, so that users are not shown errors for (or sent requests to) modules they cannot access.
 */
export const useDueDiligenceDashboard = (args: UseDueDiligenceDashboardArgs): { dashlets: DashletProps[]; modal: JSX.Element | null } => {
    const navigate = useNavigate();
    const { activeOrganizations, activeAssessmentSubjects, organizationsError } = useActiveOrganizationsAndAssessmentSubjectsQuery(args.moduleApi, args.userIsAuthorized);
    const [displayedModal, setDisplayedModal] = useState<DisplayedModal>({ type: 'none' });

    const isUcrm = args.module === ProductModule.UCRM;
    const dashboardType = isUcrm ? DashboardType.UCRM : DashboardType.THIRD_PARTY_RISK_MANAGEMENT;
    const listingPath = generatePath(isUcrm ? UCRM_BUSINESSES_AND_USE_CASES_LISTING : TPRM_THIRD_PARTIES_AND_SERVICES_LISTING);

    const inherentRiskDonutChart = useMemo(() => {
        if (activeAssessmentSubjects) {
            const scores: number[] = activeAssessmentSubjects.map((assessmentSubject) => assessmentSubject.inherent_risk_score ?? 0);
            return buildRiskRatingsChartData(scores, activeAssessmentSubjects.length);
        }
    }, [activeAssessmentSubjects]);

    const controlEffectivenessDonutChart = useMemo(() => {
        if (activeAssessmentSubjects) {
            const scores = activeAssessmentSubjects.map((assessmentSubject) => assessmentSubject.control_effectiveness ?? 0);
            return buildEffectivenessChartData(scores, activeAssessmentSubjects.length);
        }
    }, [activeAssessmentSubjects]);

    const residualRiskDonutChart = useMemo(() => {
        if (activeAssessmentSubjects) {
            const scores = activeAssessmentSubjects.map((assessmentSubject) => assessmentSubject.residual_risk_score ?? 0);
            return buildRiskRatingsChartData(scores, activeAssessmentSubjects.length);
        }
    }, [activeAssessmentSubjects]);

    const overdueAssessmentSubjects: DueDiligenceDashboardAssessmentSubject[] | undefined = useMemo(() => {
        return activeAssessmentSubjects?.filter((assessmentSubject) => assessmentSubject.is_overdue);
    }, [activeAssessmentSubjects]);

    const displayAssessmentSubjects = (assessmentSubjects: DueDiligenceDashboardAssessmentSubject[], subtitle: string): void => {
        setDisplayedModal({
            type: 'assessmentSubjects',
            subtitle,
            selectedAssessmentSubjects: assessmentSubjects.map((assessmentSubject) => ({
                assessmentSubjectId: assessmentSubject.id,
                assessmentSubjectName: assessmentSubject.name,
                organizationId: assessmentSubject.vendor_id,
                organizationName: assessmentSubject.vendor_name,
            })),
        });
    };

    const handleEffectivenessChartClick = (effectiveness: string): void => {
        const effectivenessScore = effectivenessLabelAsNumber(effectiveness);
        // Special logic needed here because UNRATED and undefined should be considered equivalent.
        displayAssessmentSubjects(
            activeAssessmentSubjects!.filter((assessmentSubject) => assessmentSubject.control_effectiveness === effectivenessScore || (effectivenessScore === Effectiveness.UNRATED && assessmentSubject.control_effectiveness === undefined)),
            effectiveness
        );
    };

    const handleInherentRiskChartClick = (targetRating: string): void => {
        const targetRatingScore = riskRatingLabelAsNumber(targetRating);
        // Special logic is not _currently_ needed here, as the inherent risk score is always defined. However, the same special logic as below is included here to prevent future bugs if inherent risk scores are ever undefined in the future.
        displayAssessmentSubjects(
            activeAssessmentSubjects!.filter((assessmentSubject) => assessmentSubject.inherent_risk_score === targetRatingScore || (targetRatingScore === RiskRating.UNRATED && assessmentSubject.inherent_risk_score === undefined)),
            targetRating
        );
    };

    const handleResidualRiskChartClick = (targetRating: string): void => {
        const targetRatingScore = riskRatingLabelAsNumber(targetRating);
        // Special logic needed here because UNRATED and undefined should be considered equivalent.
        displayAssessmentSubjects(
            activeAssessmentSubjects!.filter((assessmentSubject) => assessmentSubject.residual_risk_score === targetRatingScore || (targetRatingScore === RiskRating.UNRATED && assessmentSubject.residual_risk_score === undefined)),
            targetRating
        );
    };

    const handleOverdueAssessmentChartClick = (): void => {
        if (overdueAssessmentSubjects) {
            displayAssessmentSubjects(overdueAssessmentSubjects, 'Overdue Assessments');
        }
    };

    const modal = displayedModal.type === 'assessmentSubjects' ? <AssessmentSubjectsDisplayModal hideModal={() => setDisplayedModal({ type: 'none' })} module={args.module} selectedAssessmentSubjects={displayedModal.selectedAssessmentSubjects} subtitle={displayedModal.subtitle} /> : null;

    const dashlets: DashletProps[] = [
        {
            dashlets: [
                {
                    title: isUcrm ? 'Active Businesses' : 'Active Third Parties',
                    content: <CountCallout count={activeOrganizations?.length} onclick={() => navigate(listingPath)} />,
                },
                {
                    title: isUcrm ? 'Active Use Cases' : 'Active Third-Party Services',
                    content: <CountCallout count={activeAssessmentSubjects?.length} onclick={() => navigate(listingPath)} />,
                },
                {
                    title: 'Overdue Assessments',
                    content: <CountCallout count={overdueAssessmentSubjects?.length} countHighlight={true} onclick={handleOverdueAssessmentChartClick} />,
                },
            ],
        },
        {
            dashlets: organizationsError
                ? [
                      {
                          title: 'Inherent Risk',
                          content: <Text color="white">{organizationsError}</Text>,
                      },
                      {
                          title: 'Overall Control Effectiveness',
                          content: <Text color="white">{organizationsError}</Text>,
                      },
                      {
                          title: 'Residual Risk',
                          content: <Text color="white">{organizationsError}</Text>,
                      },
                  ]
                : [
                      {
                          title: 'Inherent Risk',
                          content: <DonutCharts module={args.module} chartName="Inherent Risk" chartData={inherentRiskDonutChart} onClick={handleInherentRiskChartClick} />,
                      },
                      {
                          title: 'Overall Control Effectiveness',
                          content: <DonutCharts module={args.module} chartName="Control Effectiveness" chartData={controlEffectivenessDonutChart} onClick={handleEffectivenessChartClick} />,
                      },
                      {
                          title: 'Residual Risk',
                          content: <DonutCharts module={args.module} chartName="Residual Risk" chartData={residualRiskDonutChart} onClick={handleResidualRiskChartClick} />,
                      },
                  ],
        },
        {
            dashlets: [
                {
                    content: <CompletedAssessmentsChart dashboardApi={args.dashboardApi} module={args.module} />,
                },
                {
                    content: <UpcomingAssessmentsChart module={args.module} assessmentSubjects={activeAssessmentSubjects} assessmentSubjectsError={organizationsError} />,
                },
            ],
        },
        {
            dashlets: [
                {
                    title: 'Due Diligence Not Scheduled',
                    content: <RiskProgressCharts module={args.module} assessmentSubjects={activeAssessmentSubjects} assessmentSubjectsError={organizationsError} />,
                },
                {
                    content: <PortfolioProfileOverTimeChart dashboardApi={args.dashboardApi} module={args.module} />,
                },
            ],
        },
        {
            dashlets: [
                {
                    content: <DashboardsHistoricalIssues allIssues={args.allIssues} issuesError={args.issuesError} dashboardType={dashboardType} organizations={activeOrganizations} organizationsError={organizationsError} />,
                },
                {
                    content: <DashboardsTrackingIssues allIssues={args.allIssues} issuesError={args.issuesError} dashboardType={dashboardType} organizations={activeOrganizations} organizationsError={organizationsError} />,
                },
            ],
        },
        {
            dashlets: [
                {
                    content: <DashboardsHistoricalExceptions allExceptions={args.allExceptions} exceptionsError={args.exceptionsError} dashboardType={dashboardType} organizations={activeOrganizations} organizationsError={organizationsError} />,
                },
                {
                    content: <DashboardsTrackingExceptions allExceptions={args.allExceptions} exceptionsError={args.exceptionsError} dashboardType={dashboardType} organizations={activeOrganizations} organizationsError={organizationsError} />,
                },
            ],
        },
    ];

    return { dashlets, modal };
};
